import { supabase } from '@/services/supabase/supabase';

/**
 * Interface for feature parameters
 */
export interface FeatureParam {
  id: number;
  key: string;
  value: boolean;
  tenant_id: string;
  created_at: string;
  updated_at: string;
}

/**
 * Get a feature parameter by key
 *
 * @param key - The feature parameter key
 * @returns The feature parameter value or false if not found
 */
export async function getFeatureParam(key: string): Promise<boolean> {
  try {
    // Direct query to get the feature parameter
    const { data, error } = await supabase
      .from('feature_params')
      .select('value')
      .eq('key', key)
      .maybeSingle();

    if (error) {
      console.error('Error getting feature parameter:', error);
      return false;
    }

    return data?.value || false;
  } catch (error) {
    console.error('Error in getFeatureParam:', error);
    return false;
  }
}

/**
 * Set a feature parameter value
 *
 * @param key - The feature parameter key
 * @param value - The feature parameter value
 * @returns True if successful, false otherwise
 */
export async function setFeatureParam(key: string, value: boolean): Promise<boolean> {
  try {
    // Check if the parameter already exists
    const { data: existingParam, error: checkError } = await supabase
      .from('feature_params')
      .select('id')
      .eq('key', key)
      .maybeSingle();

    if (checkError) {
      console.error('Error checking feature parameter:', checkError);
      return false;
    }

    if (existingParam) {
      // Update existing parameter
      const { error: updateError } = await supabase
        .from('feature_params')
        .update({ value, updated_at: new Date().toISOString() })
        .eq('id', existingParam.id);

      if (updateError) {
        console.error('Error updating feature parameter:', updateError);
        return false;
      }
    } else {
      // Insert new parameter
      const { error: insertError } = await supabase
        .from('feature_params')
        .insert([{ key, value }]);

      if (insertError) {
        console.error('Error inserting feature parameter:', insertError);
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Error in setFeatureParam:', error);
    return false;
  }
}

/**
 * Get all feature parameters
 *
 * @returns Array of feature parameters or empty array if error
 */
export async function getAllFeatureParams(): Promise<FeatureParam[]> {
  try {
    const { data, error } = await supabase
      .from('feature_params')
      .select('*');

    if (error) {
      console.error('Error getting all feature parameters:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getAllFeatureParams:', error);
    return [];
  }
}

/**
 * Ensure default feature parameters exist
 * This is useful when initializing the application
 *
 * @returns True if successful, false otherwise
 */
export async function ensureDefaultFeatureParams(): Promise<boolean> {
  try {
    // Insert default parameters if they don't exist
    const { error } = await supabase
      .from('feature_params')
      .insert([{ key: 'onboarding-flow-completed', value: false }])
      .onConflict('key, tenant_id')
      .ignore();

    if (error) {
      console.error('Error ensuring default feature parameters:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in ensureDefaultFeatureParams:', error);
    return false;
  }
}

/**
 * Check if the onboarding flow is completed
 *
 * @returns True if completed, false otherwise
 */
export async function isOnboardingFlowCompleted(): Promise<boolean> {
  return getFeatureParam('onboarding-flow-completed');
}

/**
 * Mark the onboarding flow as completed
 *
 * @returns True if successful, false otherwise
 */
export async function markOnboardingFlowCompleted(): Promise<boolean> {
  return setFeatureParam('onboarding-flow-completed', true);
}

/**
 * Reset the onboarding flow completion status
 *
 * @returns True if successful, false otherwise
 */
export async function resetOnboardingFlowCompletion(): Promise<boolean> {
  return setFeatureParam('onboarding-flow-completed', false);
}
